/**
 * Editor state — Zustand store.
 *
 * Single source of truth for:
 *   - the underlying Topology JSON
 *   - React Flow nodes + edges (derived from topology + overrides)
 *   - selection / active tool state
 *   - terminals-visible toggle
 *   - undo/redo history (snapshots of nodes + edges)
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Node, Edge } from '@xyflow/react';
import { computePinKeys } from '../layout/pinKey';
import type { Topology } from '../types/topology';
import { samplePresets } from './presets';

export type SldNode = Node<{
  symbolId: string;   // key in SYMBOLS
  label?: string;
  sublabel?: string;
  rotation?: 0 | 90 | 180 | 270;
  /** Per-instance content override (used by panel strings for dynamic count display) */
  customContent?: string;
  /** Per-instance native box override (for variable-width composites like strings) */
  customBox?: { w: number; h: number };
  /** Per-instance terminal positions override. Required for composites
   *  whose terminal Y depends on dynamic content (e.g. panel strings,
   *  where the rails sit at variable heights based on label presence
   *  and panel count). When present, takes precedence over the
   *  symbol library's static terminals. Keyed by terminal id, same
   *  shape as Terminal in types/symbol.ts. */
  customTerminals?: Record<string, { id: string; x: number; y: number; type: 'dc+' | 'dc-' | 'dc' | 'ac' | 'earth' | 'any' }>;
  /** Reference back to topology entity (inverter id, battery id, etc.) */
  topologyRef?: { kind: 'inverter' | 'battery' | 'panel' | 'misc'; id: string };
  /** Mark this symbol as a pre-existing (retrofit) item. SldSymbolNode
   *  wraps existing symbols with a dashed violet outline + EXISTING
   *  badge — standard SLD convention so installers can immediately
   *  see what's already on site vs what's being newly added. */
  isExisting?: boolean;
  /** For FREE-FORM panel-string blocks (dragged from the palette,
   *  not generated by autoLayout from an inverter's MPPT). Stores the
   *  per-instance config so click-to-edit can read/update it directly.
   *  When undefined the block is "managed" — its content comes from
   *  topology.inverters[].mppts and editing flows through the MPPT
   *  layout editor instead. */
  freeStringConfig?: {
    panels: number;
    parallels: number;
    /** References topology.panelTypes[*].id. When undefined the block
     *  uses the first registered panel type (or legacy topology.panels). */
    panelTypeId?: string;
  };
}>;

export type SldEdge = Edge<{
  /** Electrical wire style */
  style?: 'dc+' | 'dc-' | 'dc' | 'ac' | 'earth' | 'any';
  /** Free-form cable label rendered at the wire's midpoint (e.g.
   *  "6mm² DC", "32A AC", "PE 16mm²"). Empty/undefined = no label. */
  label?: string;
}>;

export type PaperSize = 'A4-landscape' | 'A3-landscape' | 'A3-portrait';

/** Page dimensions in pixels at 96 DPI */
export const PAPER_DIMENSIONS: Record<PaperSize, { w: number; h: number; label: string }> = {
  'A4-landscape': { w: 1123, h: 794,  label: 'A4 Landscape - 297 x 210 mm' },
  'A3-landscape': { w: 1587, h: 1123, label: 'A3 Landscape - 420 x 297 mm' },
  'A3-portrait':  { w: 1123, h: 1587, label: 'A3 Portrait - 297 x 420 mm' }
};

/**
 * Undo/redo history.
 *
 * We snapshot { nodes, edges } at COMMIT POINTS only (drag-stop, add,
 * delete, connect, property edit) - not on every drag tick - to keep
 * the stack human-meaningful. Each undo pops the past stack, pushes
 * the current state onto the future stack, and applies the popped
 * snapshot. Redo is the mirror.
 *
 * Loading a topology (preset switch) clears the history - it is a
 * "new document" boundary; you cannot undo past it.
 *
 * MAX_HISTORY caps the stack so a long editing session cannot blow
 * out memory.
 */
interface HistorySnapshot {
  nodes: SldNode[];
  edges: SldEdge[];
}

const MAX_HISTORY = 50;

interface EditorState {
  topology: Topology;
  nodes: SldNode[];
  edges: SldEdge[];
  paperSize: PaperSize;

  /** SP project / revision context. Set when the editor is opened
   *  with ?project=<qid> in the URL and the revisions API resolved
   *  to a backed-up revision. Stays null in standalone mode (no
   *  ?project param) — the editor falls back to localStorage in
   *  that case and never hits the revisions API. */
  projectId:  string | null;
  revisionId: number | null;
  revisionLabel: string;
  revisionSource: 'auto' | 'manual' | null;

  /** Auto-save status for the current revision. Only meaningful in
   *  project mode (revisionId !== null). The hook in
   *  hooks/useAutoSaveRevision.ts drives these; the toolbar reads
   *  them to render a "Saving… / Saved · 12:34" indicator. */
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: number | null;

  /** Snapshot of the SP project's equipment summary, taken every
   *  time we fetch project-topology.php. Powers the "Linked
   *  Project" card so the user has a static reference of what's
   *  in SP (vs. what's in the current SLD revision, which they
   *  may have edited). Null in standalone mode.
   *
   *  Capacities are pre-computed at snapshot time so the card can
   *  render them without re-walking the topology on every render
   *  AND so a mismatch comparison against the live SLD topology
   *  is just a numeric diff. Units:
   *    - panelKwp:      kilowatts peak (DC) — sum of pmax * count
   *    - inverterKwAc:  kilowatts AC — sum of inverter capacities
   *    - batteryKwh:    kilowatt-hours of usable storage */
  projectSummary: {
    customerName: string;
    address: string;
    panels: number;
    panelKwp: number;
    inverters: number;
    inverterKwAc: number;
    batteries: number;
    batteryKwh: number;
    capturedAt: number;
  } | null;

  // History stacks. Stored in the store so components can react to
  // canUndo/canRedo changes via shallow selectors.
  history: { past: HistorySnapshot[]; future: HistorySnapshot[] };

  /** Replace topology (e.g. load preset). Triggers re-layout + clears history. */
  loadTopology: (t: Topology) => void;

  /** Update nodes / edges (from React Flow onNodesChange etc).
   *  Does NOT push history - call pushHistory() at your commit point. */
  setNodes: (ns: SldNode[] | ((prev: SldNode[]) => SldNode[])) => void;
  setEdges: (es: SldEdge[] | ((prev: SldEdge[]) => SldEdge[])) => void;

  /** Paper size / orientation */
  setPaperSize: (p: PaperSize) => void;

  /** Capture current { nodes, edges } onto the past stack. Clears the
   *  future stack (standard semantics: a new edit invalidates redo). */
  pushHistory: () => void;
  undo: () => void;
  redo: () => void;

  /** Apply a functional update to topology.project. Used by the
   *  project-info form in PropertiesPanel. Doesn't trigger autoLayout
   *  (preserves user node positions); App.tsx watches topology and
   *  refreshes JUST the title block node. */
  updateProject: (updater: (p: Topology['project']) => Topology['project']) => void;

  /** General topology updater for equipment changes (panels, inverters,
   *  batteries). For non-trivial changes (adding/removing inverters)
   *  we DO want autoLayout to re-run since the structural shape of the
   *  diagram has changed — caller is expected to clear nodes/edges
   *  alongside the topology change OR accept that only the title block
   *  refreshes (BOM tables + counts). */
  updateTopology: (updater: (t: Topology) => Topology) => void;

  /** Replace topology equipment AND clear nodes/edges so autoLayout fires. */
  applyTopologyAndRelayout: (updater: (t: Topology) => Topology) => void;

  /** Hand-placed node positions, keyed by layout pin key (see
   *  layout/pinKey.ts). Recorded when the user finishes dragging a
   *  node; re-applied by App.tsx after every autoLayout regen so a
   *  structural change (toggle EXISTING, swap a model, add a battery)
   *  no longer snaps moved symbols back to their auto position. */
  pinnedPositions: Record<string, { x: number; y: number }>;
  /** Record the current position of a node as a user pin. */
  pinNodePosition: (nodeId: string) => void;

  /** Set the SP project/revision context (called by the URL handoff
   *  when the editor is opened with ?project=<qid>). Triggers no
   *  rendering side-effects — the topology load is handled
   *  separately so callers can choose to skip it (e.g. a revision
   *  switch that just updates metadata without reload). */
  setProjectContext: (ctx: {
    projectId: string;
    revisionId: number;
    revisionLabel: string;
    revisionSource: 'auto' | 'manual';
  }) => void;

  /** Auto-save status setter — driven by the auto-save hook. */
  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error', savedAt?: number) => void;

  /** Set the "what's currently in SP" snapshot. Called from the
   *  URL handoff loader and the Revisions menu's "From project"
   *  action whenever we hit project-topology.php. */
  setProjectSummary: (summary: EditorState['projectSummary']) => void;

  /** Currently-selected node id, kept in the store so any component
   *  (e.g. equipment-summary "Edit" buttons in the PropertiesPanel) can
   *  programmatically focus a symbol on the canvas. App.tsx still owns
   *  the click → selection mechanics; this just shares the resulting id. */
  selectedNodeId: string | null;
  setSelectedNodeId: (id: string | null) => void;

  /** In-app confirm dialog state. Replaces window.confirm() AND
   *  window.alert() so every modal interaction gets the styled
   *  treatment instead of the native browser dialog. `singleButton`
   *  collapses to an alert-style modal with just the confirm button
   *  — used for "OK, got it" notifications where Cancel doesn't make
   *  sense. `tone` drives the icon (info / warning / danger). */
  confirmState: {
    open: boolean;
    title: string;
    message: string;
    danger?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    singleButton?: boolean;
    tone?: 'info' | 'warning';
  };
  /** Show the confirm/alert dialog. Returns a promise that resolves
   *  with the user's choice (true = confirmed, false = cancelled).
   *  For alert-style dialogs pass `singleButton: true`. */
  askConfirm: (opts: {
    title: string;
    message: string;
    danger?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    singleButton?: boolean;
    tone?: 'info' | 'warning';
  }) => Promise<boolean>;
  /** Internal: ConfirmDialog calls this with the user's choice. */
  respondConfirm: (answer: boolean) => void;
}

// Module-scope promise resolver for the confirm dialog. Lives outside
// the Zustand store because Zustand can't serialise functions and we
// don't want to persist this transient state anyway.
let pendingConfirmResolver: ((b: boolean) => void) | null = null;

// Persist middleware writes a snapshot of (topology, nodes, edges, paperSize)
// to localStorage on every store mutation, and rehydrates from it on app
// startup. This makes the standalone (no-account) tool useful — your work
// survives browser refreshes without needing to sign in.
//
// We deliberately exclude `history` because the undo/redo stacks could
// grow large.
export const useEditorStore = create<EditorState>()(persist((set, get) => ({
  topology: samplePresets['sma-sample'].topology,
  nodes: [],
  edges: [],
  paperSize: 'A4-landscape',
  projectId: null,
  revisionId: null,
  revisionLabel: '',
  revisionSource: null,
  saveStatus: 'idle',
  lastSavedAt: null,
  projectSummary: null,
  history: { past: [], future: [] },

  loadTopology: (t) => {
    // New document boundary: wipe the history so undo cannot reach back
    // across the topology load (would be confusing UX). Also clear
    // nodes/edges so App.tsx's topology-watching effect re-runs
    // autoLayout cleanly — without this, persisted nodes from the
    // PREVIOUS topology would block the layout regen.
    set({ topology: t, nodes: [], edges: [], history: { past: [], future: [] }, pinnedPositions: {} });
  },

  setNodes: (ns) =>
    set((state) => ({
      nodes: typeof ns === 'function' ? ns(state.nodes) : ns
    })),

  setEdges: (es) =>
    set((state) => ({
      edges: typeof es === 'function' ? es(state.edges) : es
    })),

  setPaperSize: (p) => set({ paperSize: p }),

  setProjectContext: (ctx) => set({
    projectId:      ctx.projectId,
    revisionId:     ctx.revisionId,
    revisionLabel:  ctx.revisionLabel,
    revisionSource: ctx.revisionSource,
    // Reset save status when switching revision — the new revision
    // is freshly loaded, no edits to save yet.
    saveStatus:     'idle',
    lastSavedAt:    Date.now(),
  }),

  setSaveStatus: (status, savedAt) => set({
    saveStatus: status,
    ...(savedAt !== undefined ? { lastSavedAt: savedAt } : {}),
  }),

  setProjectSummary: (summary) => set({ projectSummary: summary }),

  pushHistory: () => {
    const { nodes, edges, history } = get();
    // Capture by reference - we never mutate nodes/edges arrays in
    // place, we always replace them, so reference equality holds.
    const snap: HistorySnapshot = { nodes, edges };
    const next = [...history.past, snap];
    if (next.length > MAX_HISTORY) next.shift();
    set({ history: { past: next, future: [] } });
  },

  undo: () => {
    const { nodes, edges, history } = get();
    if (history.past.length === 0) return;
    const prev = history.past[history.past.length - 1];
    const past = history.past.slice(0, -1);
    const future = [...history.future, { nodes, edges }];
    set({ nodes: prev.nodes, edges: prev.edges, history: { past, future } });
  },

  updateProject: (updater) => {
    set((state) => ({
      topology: { ...state.topology, project: updater(state.topology.project) }
    }));
  },

  updateTopology: (updater) => {
    set((state) => ({ topology: updater(state.topology) }));
  },

  applyTopologyAndRelayout: (updater) => {
    set((state) => {
      // Preserve user-added ANNOTATION nodes (the EXISTING wrapper boxes
      // created via multi-select) across re-layouts. Without this, any
      // structural topology change (toggling isExisting in PanelEditor,
      // adding an inverter, swapping a battery, etc) wipes the wrappers
      // — confusing for users who set them up explicitly. Topology-
      // derived nodes (auto-laid symbols + their wires) are still
      // cleared so autoLayout produces fresh ones.
      const annotations = state.nodes.filter((n) =>
        n.id.startsWith('existing-boundary-')
      );
      return {
        topology: updater(state.topology),
        nodes: annotations,
        edges: [],
        history: { past: [], future: [] },  // structural change ≈ new document
        selectedNodeId: null,                // can't keep selection across regen
      };
    });
  },

  pinnedPositions: {},
  pinNodePosition: (nodeId) => {
    set((state) => {
      const node = state.nodes.find((n) => n.id === nodeId);
      if (!node) return {};
      // Chrome / annotation nodes keep their own position handling.
      if (nodeId === '__page_frame__' || nodeId === '__title_block__' || nodeId.startsWith('existing-boundary-')) return {};
      const key = computePinKeys(state.nodes).get(nodeId);
      if (!key) return {};
      return {
        pinnedPositions: {
          ...state.pinnedPositions,
          [key]: { x: node.position.x, y: node.position.y },
        },
      };
    });
  },

  selectedNodeId: null,
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),

  // Confirm dialog — module-level Promise resolver so askConfirm() can
  // suspend until the user picks an option. Stashed outside the store
  // (in module scope) because Zustand serialisation chokes on functions,
  // and we don't need it persisted anyway.
  confirmState: { open: false, title: '', message: '' },
  askConfirm: (opts) => {
    return new Promise<boolean>((resolve) => {
      pendingConfirmResolver = resolve;
      set({ confirmState: { ...opts, open: true } });
    });
  },
  respondConfirm: (answer) => {
    const r = pendingConfirmResolver;
    pendingConfirmResolver = null;
    set({ confirmState: { open: false, title: '', message: '' } });
    if (r) r(answer);
  },

  redo: () => {
    const { nodes, edges, history } = get();
    if (history.future.length === 0) return;
    const next = history.future[history.future.length - 1];
    const future = history.future.slice(0, -1);
    const past = [...history.past, { nodes, edges }];
    set({ nodes: next.nodes, edges: next.edges, history: { past, future } });
  }
}), {
  name: 'sld-editor-state-v1',
  storage: createJSONStorage(() => localStorage),
  // Only persist the durable parts of state. History (undo/redo stack) is
  // session-scoped and can grow large. A version field lets us evict old
  // snapshots if the schema changes incompatibly later.
  partialize: (s) => ({
    topology: s.topology,
    nodes: s.nodes,
    edges: s.edges,
    paperSize: s.paperSize,
    pinnedPositions: s.pinnedPositions,
  }),
  version: 1,
}));
